import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AdminLayout } from "./components/layout/Layout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { UsersPage } from "./pages/UsersPage";
import { MenuPage } from "./pages/MenuPage";
import { CategoriesPage } from "./pages/CategoriesPage";
import { DealsPage } from "./pages/DealsPage";
import { TablesPage } from "./pages/TablesPage";
import { OrdersPage } from "./pages/OrdersPage";
import { ReportsPage } from "./pages/ReportsPage";
import { FinancialPage } from "./pages/FinancialPage";
import { InventoryPage } from "./pages/InventoryPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { POSPage } from "./pages/POSPage";

// Role-based access control
const PERMS: Record<string, string[]> = {
	dashboard: ["admin", "manager", "cashier"],
	pos: ["cashier"],
	orders: ["admin", "manager", "cashier"],
	tables: ["admin", "manager", "cashier"],
	menu: ["admin", "manager"],
	categories: ["admin", "manager"],
	deals: ["admin", "manager", "cashier"],
	users: ["admin"],
	payments: ["admin", "manager"],
	reports: ["admin", "manager"],
	financial: ["admin", "manager"],
	inventory: ["admin", "manager"],
	settings: ["admin", "manager"],
};

const TITLES: Record<string, string> = {
	dashboard: "Dashboard",
	pos: "Open POS",
	orders: "Orders",
	tables: "Tables",
	menu: "Menu Items",
	categories: "Categories",
	deals: "Deals & Promos",
	users: "Users & Roles",
	payments: "Payments",
	reports: "Sales Reports",
	financial: "Financial Reports",
	inventory: "Inventory",
	settings: "Settings",
};

function Guard({ id, children }: { id: string; children: React.ReactNode }) {
	const { user } = useAuth();
	if (!user) return <Navigate to="/login" replace />;
	if (!(PERMS[id] || ["admin"]).includes(user.role))
		return <Navigate to="/dashboard" replace />;

	// POS page renders its own full-screen layout
	if (id === "pos") return <>{children}</>;

	return <AdminLayout title={TITLES[id] || id}>{children}</AdminLayout>;
}

function AppRoutes() {
	const { user, loading } = useAuth();

	if (loading)
		return (
			<div
				style={{
					height: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "var(--bg)",
				}}>
				<div className="spinner" style={{ width: 32, height: 32 }} />
			</div>
		);

	if (!user)
		return (
			<Routes>
				<Route path="/login" element={<LoginPage />} />
				<Route path="*" element={<Navigate to="/login" replace />} />
			</Routes>
		);

	// Default page per role
	const home = user.role === "cashier" ? "/pos" : "/dashboard";

	return (
		<Routes>
			<Route path="/" element={<Navigate to={home} replace />} />
			<Route path="/login" element={<Navigate to={home} replace />} />
			<Route
				path="/dashboard"
				element={
					<Guard id="dashboard">
						<DashboardPage />
					</Guard>
				}
			/>
			<Route
				path="/pos"
				element={
					<Guard id="pos">
						<POSPage />
					</Guard>
				}
			/>
			<Route
				path="/orders"
				element={
					<Guard id="orders">
						<OrdersPage />
					</Guard>
				}
			/>
			<Route
				path="/tables"
				element={
					<Guard id="tables">
						<TablesPage />
					</Guard>
				}
			/>
			<Route
				path="/menu"
				element={
					<Guard id="menu">
						<MenuPage />
					</Guard>
				}
			/>
			<Route
				path="/categories"
				element={
					<Guard id="categories">
						<CategoriesPage />
					</Guard>
				}
			/>
			<Route
				path="/deals"
				element={
					<Guard id="deals">
						<DealsPage />
					</Guard>
				}
			/>
			<Route
				path="/users"
				element={
					<Guard id="users">
						<UsersPage />
					</Guard>
				}
			/>
			<Route
				path="/payments"
				element={
					<Guard id="payments">
						<PaymentsPage />
					</Guard>
				}
			/>
			<Route
				path="/reports"
				element={
					<Guard id="reports">
						<ReportsPage />
					</Guard>
				}
			/>
			<Route
				path="/financial"
				element={
					<Guard id="financial">
						<FinancialPage />
					</Guard>
				}
			/>
			<Route
				path="/inventory"
				element={
					<Guard id="inventory">
						<InventoryPage />
					</Guard>
				}
			/>
			<Route
				path="/settings"
				element={
					<Guard id="settings">
						<SettingsPage />
					</Guard>
				}
			/>
			<Route path="*" element={<Navigate to={home} replace />} />
		</Routes>
	);
}

export default function App() {
	return (
		<BrowserRouter>
			<AuthProvider>
				<AppRoutes />
			</AuthProvider>
		</BrowserRouter>
	);
}
